import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import { RIFLE_TOURNAMENT_SLUG, type ShooterGender } from '../lib/shooting';
import type { ScoringTeam } from '../lib/scoring-navigation';

export interface TournamentSummary {
  id: string;
  slug: string;
  title: string;
  status: string;
  /** Shots in the round (15). Missing from an API that predates per-shot scoring. */
  shotsPlanned?: number;
  /** Best shots that count (10). */
  shotsCounted?: number;
  maxDisplay?: string;
  /** Listed on /scoreboard and the home page's Live button. */
  published?: boolean;
}

export interface ShooterRow {
  id: string;
  name: string;
  bib: string | null;
  teamName?: string | null;
  sortOrder: number;
  /** Men / Women tag; null = not set. */
  gender?: ShooterGender | null;
}

export interface ScoreRow {
  id: string;
  shooterId: string;
  seriesNumber: number;
  shotNumber: number;
  pointsDisplay: string;
  innerTens: number;
  updatedAt: string;
}

export interface LeaderboardRow {
  shooterId: string;
  rank: number;
  totalDisplay: string;
  innerTens: number;
  tens?: number;
  shotsFired?: number;
  shotsPlanned?: number;
  name?: string;
  bib?: string | null;
  teamName?: string | null;
  gender?: ShooterGender | null;
  shots?: { shotNumber: number; pointsDisplay: string; counted?: boolean }[];
}

export interface TournamentDetail {
  ok: boolean;
  tournament: TournamentSummary;
  shooters: ShooterRow[];
  scores: ScoreRow[];
  leaderboard: { rows: LeaderboardRow[]; updatedAt?: string | null; displayPage?: number; round1?: { teams: (ScoringTeam & { totalDisplay: string })[] } };
  /** Suggested RFL number for the next shooter added by hand. */
  nextRfl?: string;
  /** Registration prefix for this tournament's numbers (RFL for rifle). */
  bibPrefix?: string;
  /** Event-day tournament mode. Missing from an older API means off. */
  eventDay?: boolean;
}

/**
 * Kept across page changes so going list -> shooter -> back paints at once (and the list can
 * restore its scroll) while a fresh copy loads behind it.
 */
let cachedTournaments: TournamentSummary[] | null = null;
const cachedDetails = new Map<string, TournamentDetail>();

export function resetScoringCacheForTests(): void {
  cachedTournaments = null;
  cachedDetails.clear();
}

export interface ScoringTournamentState {
  tournaments: TournamentSummary[] | null;
  /** The slug in use: the one asked for, or the rifle tournament by default. */
  slug: string;
  detail: TournamentDetail | null;
  /** True once tournaments loaded and none matched the slug. */
  notFound: boolean;
  error: string | null;
  setDetail: (update: (current: TournamentDetail) => TournamentDetail) => void;
  /** Add or replace a tournament in the list (after creating one or changing its format). */
  putTournament: (tournament: TournamentSummary) => void;
}

export function useScoringTournament(requestedSlug?: string): ScoringTournamentState {
  const [tournaments, setTournaments] = useState<TournamentSummary[] | null>(cachedTournaments);
  const [error, setError] = useState<string | null>(null);

  const fallbackSlug =
    tournaments?.find((item) => item.slug === RIFLE_TOURNAMENT_SLUG)?.slug ?? tournaments?.[0]?.slug ?? RIFLE_TOURNAMENT_SLUG;
  const slug = requestedSlug ?? fallbackSlug;
  const tournament = tournaments?.find((item) => item.slug === slug) ?? null;

  const [detail, setDetailState] = useState<TournamentDetail | null>(() => cachedDetails.get(slug) ?? null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ ok: boolean; tournaments: TournamentSummary[] }>('/api/admin/shooting/tournaments')
      .then((body) => {
        if (cancelled) return;
        cachedTournaments = body.tournaments;
        setTournaments(body.tournaments);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load scoring.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const tournamentId = tournament?.id ?? null;
  useEffect(() => {
    setDetailState(cachedDetails.get(slug) ?? null);
    if (!tournamentId) return;
    let cancelled = false;
    apiFetch<TournamentDetail>(`/api/admin/shooting/tournaments/${tournamentId}`)
      .then((body) => {
        if (cancelled) return;
        cachedDetails.set(slug, body);
        setDetailState(body);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load shooters.');
      });
    return () => {
      cancelled = true;
    };
  }, [slug, tournamentId]);

  const setDetail = useCallback(
    (update: (current: TournamentDetail) => TournamentDetail) => {
      setDetailState((current) => {
        if (!current) return current;
        const next = update(current);
        cachedDetails.set(slug, next);
        return next;
      });
    },
    [slug],
  );

  const putTournament = useCallback((next: TournamentSummary) => {
    setTournaments((current) => {
      const list = current ?? [];
      const updated = list.some((item) => item.id === next.id)
        ? list.map((item) => (item.id === next.id ? { ...item, ...next } : item))
        : [...list, next];
      cachedTournaments = updated;
      return updated;
    });
  }, []);

  return {
    tournaments,
    putTournament,
    slug,
    detail: detail && detail.tournament.slug === slug ? detail : null,
    notFound: Boolean(tournaments && !tournament),
    error,
    setDetail,
  };
}

